/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import './AccountFilterModal.scss';

import React from 'react';
import { connect } from 'react-redux';
import Store from 'state/Store';
import { clone } from 'lodash';

import { preFilterAccounts } from 'state/Actions';
import { yyyymmdd2mmmddyyyy } from 'services/Dates';
import BaseModal from '../../../../shared/Modal/BaseModal';
import { ReactComponent as FilterIcon } from 'assets/icons/filter.svg';

import AccountsTable from './AccountsTable';

class AccountFilterModal extends BaseModal {
  constructor(props) {
    super(props);

    this.updateSubset = this.updateSubset.bind(this);
    this.updateStore = this.updateStore.bind(this);
    this.cancel = this.cancel.bind(this);
  }

  componentDidMount() {
    const { subset, all } = this.props.accountsPreFilter;
    this.setState({
      showModal: false,
      currentSubset: subset,
      all,
      showWarning: false,
      changeRegistered: false,
      lastAccountToggled: null
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.accountsPreFilter !== prevProps.accountsPreFilter) {
      const { subset, all } = this.props.accountsPreFilter;

      this.setState({
        showModal: false,
        currentSubset: subset,
        all,
        showWarning: false,
        changeRegistered: false,
        lastAccountToggled: null
      });
    }
  }

  updateSubset(accountId, isSelected) {
    const tempAccounts = clone(this.state.currentSubset);
    let showWarning = false;

    if (Array.isArray(accountId)) {
      accountId.map((id) => (tempAccounts[id] = isSelected));
    } else {
      tempAccounts[accountId] = isSelected;
    }

    showWarning = Object.keys(tempAccounts).every((a) => !tempAccounts[a]);

    this.setState({
      currentSubset: tempAccounts,
      all: Object.keys(tempAccounts).every((k) => tempAccounts[k]),
      showWarning,
      changeRegistered: true,
      lastAccountToggled: Array.isArray(accountId)
        ? accountId[accountId.length - 1]
        : accountId
    });
  }

  updateStore() {
    const allToggledOff = Object.keys(this.state.currentSubset).every(
      (a) => !this.state.currentSubset[a]
    );

    if (allToggledOff) {
      let tempAccounts = clone(this.state.currentSubset);
      tempAccounts[this.state.lastAccountToggled] = true;
    }

    const visibleAccountIds = Object.keys(this.state.currentSubset).filter(
      (accountId) => this.state.currentSubset[accountId]
    );

    const accountsWithProductType = visibleAccountIds.map((accountId) => ({
      accountId,
      productType:
        this.props.accounts.find((a) => a.accountId === accountId)
          ?.productType || ''
    }));

    Store.dispatch(
      preFilterAccounts(
        this.state.all,
        this.state.currentSubset,
        accountsWithProductType
      )
    );
    this.setState({
      showWarning: false,
      changeRegistered: false
    });

    this.hideModal();
  }

  cancel() {
    const { subset, all } = this.props.accountsPreFilter;

    this.setState({
      showWarning: false,
      changeRegistered: false,
      currentSubset: subset,
      all
    });

    this.hideModal();
  }

  containerClassName() {
    return super.containerClassName() + ' modal-account-filter-container';
  }

  renderIcon() {
    return null;
  }

  renderLinkContent() {
    return (
      <div className="account-filter-container">
        <FilterIcon className="filter-icon" />
        <span className="account-filter-text">Account Filter</span>
      </div>
    );
  }

  renderModal() {
    return (
      <span className="modal-info-popup-container">
        <span className="modal-info-popup">{this.renderModalContent()}</span>
      </span>
    );
  }

  renderProduct(productType) {
    const { products, accounts, toDate } = this.props;

    const productAccounts = accounts.filter(
      (account) =>
        products.byAccount[account.productType].label === productType.label &&
        !account.purgedAccount &&
        account.balanceValue !== 0
    );

    return productAccounts.length === 0 ? null : (
      <AccountsTable
        key={productType.label}
        productName={productType.label}
        productAccounts={productAccounts}
        endingDate={toDate}
        onChange={this.updateSubset}
      />
    );
  }

  renderProducts() {
    const { productTypes } = this.props;
    return Object.keys(productTypes).map((productType) =>
      this.renderProduct(productTypes[productType])
    );
  }

  renderPurgedAccounts() {
    const { accounts, toDate } = this.props;
    const purgedAccountLabel = 'Closed Accounts';
    const tableClassName = 'closed-account';

    this.purgedAccounts = accounts.filter(
      (account) => account.purgedAccount || account.balanceValue === 0
    );

    return this.purgedAccounts.length === 0 ? null : (
      <AccountsTable
        productName={purgedAccountLabel}
        productAccounts={this.purgedAccounts}
        endingDate={toDate}
        onChange={this.updateSubset}
        tableClassName={tableClassName}
      />
    );
  }

  renderModalContent() {
    const { changeRegistered, showWarning } = this.state;

    return (
      <div>
        <div id="modal-info-content">
          <div id="accounts">
            <div className="title">Account Filter</div>
            <div className="disclosure">
              You can customize the accounts included or excluded in your
              analysis by selecting the accounts below.
            </div>
            <div className="table-wrapper">
              {this.renderProducts()}
              {this.renderPurgedAccounts()}
              {this.purgedAccounts.length !== 0 ? (
                <div className="purged-account-copy">
                  Closed accounts are those that currently have zero balances;
                  by including these accounts, any activity in these accounts
                  prior to their closing will be included in your Personal Rate
                  of Return calculation. Only accounts closed since 01/01/2005
                  are eligible for inclusion. Activity prior to 01/01/1992 is
                  not included.
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="button-container">
          {showWarning ? (
            <div className="account-warning">
              You must select at least one account.
            </div>
          ) : null}

          {changeRegistered ? (
            <a className="cancel-link" onClick={this.cancel}>
              Cancel
            </a>
          ) : null}
          <button
            className="change-filter-button"
            onClick={changeRegistered ? this.updateStore : this.cancel}
            disabled={showWarning}>
            {changeRegistered ? 'Update' : 'Close'}
          </button>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { view, accounts, productTypes, products } = state;
  const { accountsPreFilter } = state.selections;

  return {
    toDate: yyyymmdd2mmmddyyyy(view.endDate),
    accountsPreFilter,
    accounts,
    productTypes,
    products
  };
};

export default connect(mapStateToProps)(AccountFilterModal);
